/**
 * CallLogForm — Mini-Form fuer Telefonat-Erfassung (CARD-07).
 *
 * Inline-Form im TimelineTab. Felder:
 *  - Datum-Picker (datetime-local) — Default: jetzt
 *  - Dauer in Minuten (number, min=1)
 *  - Ergebnis-Notiz (textarea)
 *
 * Submit → leadsApi.logCall(id, body) → POST /leads/:id/call-log
 * Onparent meldet das neue LeadEvent zurueck (call_logged).
 *
 * CI-Farben: #E9FF57 + #1A1A1A auf Submit-Button.
 */

import { useState } from 'react';
import { leadsApi } from '../services/leadsApi';
import type { LeadEvent } from '../types';

interface Props {
  leadId: number;
  onLogged: (event: LeadEvent) => void;
  onCancel: () => void;
}

function nowDatetimeLocal(): string {
  // Format yyyy-MM-ddTHH:mm fuer <input type="datetime-local">
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CallLogForm({ leadId, onLogged, onCancel }: Props) {
  const [calledAt, setCalledAt] = useState<string>(nowDatetimeLocal());
  const [durationMinutes, setDurationMinutes] = useState<string>('5');
  const [resultNote, setResultNote] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const durationNum = Number(durationMinutes);
  const isValid =
    !Number.isNaN(durationNum) &&
    durationNum > 0 &&
    resultNote.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const event = await leadsApi.logCall(leadId, {
        calledAt: calledAt ? new Date(calledAt).toISOString() : undefined,
        durationMinutes: durationNum,
        resultNote: resultNote.trim(),
      });
      onLogged(event);
    } catch (e: any) {
      setError(e?.message ?? 'Telefonat konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#DAD9D9] rounded p-3 bg-gray-50 space-y-3">
      <h4 className="text-sm font-semibold text-[#1A1A1A]">Telefonat erfassen</h4>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
            Zeitpunkt
          </label>
          <input
            type="datetime-local"
            value={calledAt}
            onChange={(e) => setCalledAt(e.target.value)}
            className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
            Dauer (Minuten)
          </label>
          <input
            type="number"
            min={1}
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(e.target.value)}
            className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-[#1A1A1A] mb-1">
          Ergebnis-Notiz
        </label>
        <textarea
          rows={3}
          value={resultNote}
          onChange={(e) => setResultNote(e.target.value)}
          placeholder="Was wurde besprochen?"
          className="w-full border border-[#DAD9D9] rounded px-2 py-1 text-sm text-[#1A1A1A] bg-white resize-y"
        />
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-[#1A1A1A] hover:bg-gray-300 disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || saving}
          className="px-3 py-1.5 rounded text-sm font-medium bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Speichert...' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}
